import { useEffect, useState } from 'react';

import type { DataTheme } from '@remarkable/ark-web';
import { useAtom } from 'jotai';

import { IS_DEVELOPMENT } from 'src/config';

import { atomWithLocalStorage } from './atomWithLocalStorage';

type Theme = NonNullable<DataTheme['dataTheme']> | 'system';

export const themes = {
  system: 'System',
  'light-neutral': 'Light Neutral',
  'dark-neutral': 'Dark Neutral',
  'light-green': 'Light Green',
  'dark-green': 'Dark Green',
  'light-red': 'Light Red',
  'dark-red': 'Dark Red',
} as const satisfies Record<Theme, string>;

const themeAtom = atomWithLocalStorage<Theme>('theme', 'system');

export const useTheme = () => {
  const [theme, setTheme] = useAtom(themeAtom);
  const [systemTheme, setSystemTheme] = useState<Theme>('light-neutral');

  useEffect(() => {
    const colorSchemeListener = (e: MediaQueryListEvent) => {
      const newTheme = e.matches ? 'dark-neutral' : 'light-neutral';

      if (!IS_DEVELOPMENT) {
        // Until we are ready for dark mode, we will only use light-neutral in production
        setSystemTheme(newTheme);
      }
    };

    window
      .matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', colorSchemeListener);

    return () => {
      window
        .matchMedia('(prefers-color-scheme: dark)')
        .removeEventListener('change', colorSchemeListener);
    };
  }, []);

  const toggleTheme = () => {
    // get the next theme in the list, or loop back to the first one
    setTheme((prev) => {
      const currentIndex = Object.keys(themes).indexOf(prev);
      const nextTheme =
        currentIndex + 1 < Object.keys(themes).length
          ? (Object.keys(themes)[currentIndex + 1] as Theme)
          : (Object.keys(themes)[0] as Theme);

      return nextTheme;
    });
  };

  return {
    theme: theme === 'system' ? systemTheme : theme,
    isSystemTheme: theme === 'system',
    isDark: theme.includes('dark'),
    themes,
    setTheme,
    toggleTheme,
  };
};
